import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

type FullscreenEl = HTMLElement & {
	requestFullscreen?: () => Promise<void>
	webkitRequestFullscreen?: () => Promise<void>
	webkitEnterFullscreen?: () => void
}

export class ArticleVideoFullscreen extends UcoastEl {
	static htmlSelector = 'article-video-fullscreen'

	trigger: HTMLButtonElement
	onFullscreenChange: () => void

	constructor() {
		super()
		console.log('[ArticleVideoFullscreen] constructor', this)
		this.trigger = q.rs<HTMLButtonElement>('[data-uc-fullscreen-trigger]', this)
		this.trigger.addEventListener('click', this.onClick.bind(this))
		this.onFullscreenChange = this.handleFullscreenChange.bind(this)
	}

	override connectedCallback() {
		super.connectedCallback()
		document.addEventListener('fullscreenchange', this.onFullscreenChange)
		document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		document.removeEventListener('fullscreenchange', this.onFullscreenChange)
		document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
	}

	getVideo(): HTMLVideoElement | null {
		return this.querySelector('video')
	}

	onClick(event: MouseEvent) {
		event.preventDefault()
		event.stopPropagation()

		const video = this.getVideo()
		if (video) {
			video.muted = false
			video.removeAttribute('muted')
			void video.play().catch((err: unknown) => console.warn('[fullscreen] play failed', err))
		}

		// iOS Safari path — the <video> element has its own enterFullscreen API
		// that overlays a native player. requestFullscreen on a div doesn't work.
		const videoFs = video as unknown as FullscreenEl | null
		if (videoFs?.webkitEnterFullscreen) {
			try {
				videoFs.webkitEnterFullscreen()
				return
			} catch (err) {
				console.warn('[fullscreen] webkitEnterFullscreen failed', err)
			}
		}

		// Try fullscreen on the wrapper (so the rounded corners and overlay stay).
		// Fall back to the <video> element if the wrapper rejects.
		const wrapper = this as unknown as FullscreenEl
		const tryRequest = (el: FullscreenEl | null) => {
			if (!el) return Promise.reject(new Error('no element'))
			const req = el.requestFullscreen ?? el.webkitRequestFullscreen
			if (!req) return Promise.reject(new Error('no requestFullscreen on element'))
			return req.call(el)
		}

		tryRequest(wrapper).catch((wrapperErr: unknown) => {
			console.warn('[fullscreen] wrapper failed, trying video', wrapperErr)
			tryRequest(video as unknown as FullscreenEl | null).catch((videoErr: unknown) => {
				console.error('[fullscreen] all targets failed', videoErr)
			})
		})
	}

	handleFullscreenChange() {
		const fsEl = document.fullscreenElement ?? (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement
		// When the user exits fullscreen, re-mute the video so it doesn't keep
		// playing audio in the inline player.
		if (!fsEl || !this.contains(fsEl)) {
			const video = this.getVideo()
			if (video) {
				video.muted = true
			}
		}
	}
}
